import { SearchInput } from "@/components/common/search-input";
import Pagination from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { StatusBadge } from "@/components/ui/status-badge";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  UserManagementService,
  GetListUserManageRes,
  IStatus,
} from "@/services/user-management-service";
import { formatDate } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import IconHelp from "@/assets/icons/icon-help.svg";
import { toast, useToast } from "@/hooks/use-toast";
import { usePermissions } from "@/lib/permissions";
import IcNodata from "@/assets/icons/ic-nodata.svg";
import IcCopy from "@/assets/icons/ic-copy.svg";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TruncatedTooltip } from "@/components/ui/truncated-tooltip";

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "전체 상태" },
  { value: "active", label: "Active" },
  { value: "pending", label: "Pending" },
];

const ManageUserPage = () => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canDelete = can.delete("user_management");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [admins, setAdmins] = useState<GetListUserManageRes[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const hasFetchedRef = useRef(false);
  const { toast } = useToast();
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [approveTarget, setApproveTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isApproving, setIsApproving] = useState(false);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchAdmins = useCallback(async () => {
    if (hasFetchedRef.current) {
      setIsSearching(true);
    }
    try {
      const params: { page: number; size: number; search?: string; status?: IStatus } = {
        page,
        size: pageSize,
      };
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      if (statusFilter !== "all") {
        params.status = statusFilter as IStatus;
      }
      const res = await UserManagementService.getList(params);
      setAdmins(res.data.data.items || []);
      setTotalCount(res.data.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch admins:", error);
      setAdmins([]);
      setTotalCount(0);
    } finally {
      setIsInitialLoading(false);
      setIsSearching(false);
      hasFetchedRef.current = true;
    }
  }, [page, pageSize, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  const displayedCount = admins.length;
  const totalPages = Math.ceil(totalCount / pageSize);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await UserManagementService.deleteUserById(deleteTarget.id);
      setDeleteTarget(null);
      toast({ description: "사용자가 삭제되었습니다." });
      fetchAdmins();
    } catch (error) {
      console.error("Failed to delete user:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    setIsApproving(true);
    try {
      await UserManagementService.updateUserStatus(approveTarget.id, "active");
      setApproveTarget(null);
      toast({ description: "사용자가 승인되었습니다." });
      fetchAdmins();
    } catch {
      toast({ description: "승인에 실패했습니다.", variant: "destructive" });
    } finally {
      setIsApproving(false);
    }
  };

  const handleNavigateUserDetail = (userId: string) => {
    navigate(`/manage-user/${userId}`);
  };
  return (
    <>
      {isInitialLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="bg-white "
        style={{ display: isInitialLoading ? "none" : "block" }}
      >
      {/* Header: Search, Add Button */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {/* Search Input */}
          <SearchInput
            placeholder="이름, 이메일 또는 기업명으로 검색..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onBlur={(e) => setSearchQuery(e.target.value.trim())}
            containerClassName="w-[360px]"
          />

          {/* Status Filter */}
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[127px]">
              <SelectValue placeholder="전체 상태" />
            </SelectTrigger>
            <SelectContent>
              {STATUS_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Info Text */}
      <p className="text-sm text-[#B0B0B0] mb-3">
        전체 사용자 {totalCount}명 중 {displayedCount}명 표시 중
      </p>

      {/* Table */}
      <div
        className={`border border-[#00000017] rounded overflow-x-auto transition-opacity duration-200 ${isSearching ? "pointer-events-none" : ""}`}
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-[#EBEBEB]">
              <TableHead className="text-[#000000] text-xs uppercase tracking-wide font-medium w-[273px]  ">
                사용자
              </TableHead>
              <TableHead className="text-[#000000] text-xs uppercase tracking-wide font-medium w-[219px]">
                회사
              </TableHead>
              <TableHead className="text-[#000000] text-xs uppercase tracking-wide font-medium w-[139px]">
                상태
              </TableHead>
              <TableHead className="text-[#000000] text-xs uppercase tracking-wide font-medium w-[136px]">
                프로젝트
              </TableHead>
              <TableHead className="text-[#000000] text-xs uppercase tracking-wide font-medium w-[159px]">
                가입일
              </TableHead>
              <TableHead className="text-right text-[#000000] text-xs uppercase tracking-wide font-medium w-[122px]">
                관리
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {admins.length === 0 && !isInitialLoading && !isSearching ? (
              <TableRow>
                <TableCell colSpan={6} className="h-[600px] text-center">
                  <div className="flex flex-col items-center justify-center h-full gap-4">
                    <img src={IcNodata} alt="No data" />
                    <p className="text-[32px] text-[#000000E8]">
                      {debouncedSearch || statusFilter !== "all"
                        ? "검색 결과가 없습니다"
                        : "No data"}
                    </p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => (
                <TableRow
                  key={admin.id}
                  className="hover:bg-transparent cursor-pointer"
                  onClick={() => handleNavigateUserDetail(admin.id)}
                >
                  <TableCell className="p-[12px] max-w-[273px]">
                    <div className="flex flex-col gap-[2px]">
                      <TruncatedTooltip className="font-normal text-[16px] text-[#000000E8] truncate">
                        {admin.requester_name}
                      </TruncatedTooltip>
                      <div className="flex items-center gap-2">
                        <TruncatedTooltip className="text-xs text-[#B0B0B0]">
                          {admin.email}
                        </TruncatedTooltip>
                        <img
                          src={IcCopy}
                          alt="Copy email"
                          className="w-4 h-4 cursor-pointer shrink-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(admin.email);
                            toast({ description: "복사되었습니다" });
                          }}
                        />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="p-[12px] text-base text-[#000000E8] max-w-[219px]">
                    <TruncatedTooltip className="truncate">{admin.company_name}</TruncatedTooltip>
                  </TableCell>
                  <TableCell className="p-[12px] w-[139px]">
                    <StatusBadge
                      label={admin.status}
                      mode={admin.status === "active" ? "success" : "pending"}
                      labelClassName="capitalize "
                      className="leading-6 h-6"
                    />
                  </TableCell>
                  <TableCell className="p-[12px] text-base text-[#000000E8] w-[136px]">
                    {admin.project_count}
                  </TableCell>
                  <TableCell className="p-[12px] text-base text-[#000000] w-[159px]">
                    {formatDate(admin.created_at, "YYYY-MM-DD")}
                  </TableCell>
                  <TableCell className="p-[12px] w-[202px]">
                    <div className="flex items-center justify-end gap-2">
                      {admin.status === "pending" && canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setApproveTarget({
                              id: admin.id,
                              name: admin.requester_name,
                            });
                          }}
                          className="text-black text-[12px] font-bold px-[12px] py-[8px] border border-[rgba(0,0,0,0.15)] rounded-[4px] h-[32px] w-[80px] flex items-center justify-center"
                        >
                          승인
                        </button>
                      )}
                      {canDelete && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget({
                              id: admin.id,
                              name: admin.requester_name,
                            });
                          }}
                          className="text-[#000000] text-[12px] font-bold px-[12px] py-[8px] bg-[#F7DDCF] rounded-[4px] w-[80px] h-[32px]"
                        >
                          삭제
                        </button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!isInitialLoading && admins.length > 0 && (
        <Pagination
          className="justify-center mt-6"
          currentPage={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(p)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        icon={<img src={IconHelp} alt="Icon Alert Circle" />}
        variant="danger"
        title="사용자 삭제 확인"
        description={`해당 프로젝트를 삭제하시겠습니까?`}
        confirmText="네"
        cancelText="아니요"
        onConfirm={handleDelete}
        loading={isDeleting}
      />

      {/* Approval Confirmation Dialog */}
      <ConfirmDialog
        open={!!approveTarget}
        onOpenChange={(open) => !open && setApproveTarget(null)}
        icon={<img src={IconHelp} alt="Icon Alert Circle" />}
        variant="success"
        title="사용자 승인 확인"
        description={`${approveTarget?.name || "해당 사용자"}님의 계정을 승인하시겠습니까?\n승인 전에 반드시 사용자의 이메일을 Azure Entra ID App에 수동으로 등록했는지 확인하세요. 등록되지 않은 경우 SSO 로그인이 불가능합니다.`}
        confirmText="네"
        cancelText="아니요"
        onConfirm={handleApprove}
        loading={isApproving}
      />
    </div>
    </>
  );
};

export default ManageUserPage;
